import type { AddSpanRequest } from '@verantos-dev/core-annotation-tool';
import {
  annotationToolApiV1,
  getAnnotationToolBasePath,
  type AppDispatch,
  type ObjectResponse,
  type RootState,
} from '@verantos-dev/core-annotation-tool';

import { SUPERVISOR_TAG } from '../constants';
import { CompleteSupervisionRequest } from '../types/request';
import type {
  SupervisorAddFinalizedAnnotationRequest,
  SupervisorCreateAnnotationRequest,
  SupervisorMutationResponse,
  SupervisorWorkspaceResponse,
  UpdateSupervisorAttributesRequest,
} from '../types/response';

// Recovers { encounterId, batchId } from the active getSupervisorWorkspace
// cache entry via RTK Query's own cached-args lookup. Used by mutations
// whose args don't include encounterId/batchId directly.
function getActiveWorkspaceArgs(state: RootState) {
  return supervisorApiSlice.util.selectCachedArgsForQuery(state, 'getSupervisorWorkspace')[0];
}

function patchWorkspaceCache(
  dispatch: AppDispatch,
  encounterId: string,
  batchId: string,
  data: ObjectResponse<SupervisorMutationResponse>
) {
  dispatch(
    supervisorApiSlice.util.updateQueryData(
      'getSupervisorWorkspace',
      { encounterId, batchId },
      (draft) => {
        if (data.data.annotationsReview) {
          draft.data.annotationsReview = data.data.annotationsReview;
        }
        if (data.data.taskStatus) {
          draft.data.task.status = data.data.taskStatus;
        }
      }
    )
  );
}

export const supervisorApiSlice = annotationToolApiV1
  .enhanceEndpoints({
    addTagTypes: [SUPERVISOR_TAG],
  })
  .injectEndpoints({
    overrideExisting: true,
    endpoints: (builder) => ({
      getSupervisorWorkspace: builder.query<
        ObjectResponse<SupervisorWorkspaceResponse>,
        {
          encounterId: string;
          batchId: string;
        }
      >({
        query: ({ encounterId, batchId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return `${tempPath}/work-queue/tasks/${batchId}/${encounterId}/supervisor-workspace`;
        },
        providesTags: [SUPERVISOR_TAG],
      }),

      resolveSupervisorConflict: builder.mutation<
        ObjectResponse<SupervisorMutationResponse>,
        {
          encounterId: string;
          batchId: string;
          annotationIdToAccept: string;
          annotationIdToReject?: string;
        }
      >({
        query: ({ encounterId, batchId, annotationIdToAccept, annotationIdToReject = '' }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/work-queue/tasks/${batchId}/${encounterId}/supervisor-workspace/resolve`,
            method: 'POST',
            body: {
              annotationIdToAccept,
              annotationIdToReject,
            },
          };
        },
        async onQueryStarted({ encounterId, batchId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            patchWorkspaceCache(dispatch, encounterId, batchId, data);
          } catch {
            /* cache patch skipped on error — UI keeps prior state */
          }
        },
      }),

      rejectConflictAnnotation: builder.mutation<
        ObjectResponse<SupervisorMutationResponse>,
        {
          encounterId: string;
          batchId: string;
          annotationIdToReject: string;
        }
      >({
        query: ({ encounterId, batchId, annotationIdToReject }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/work-queue/tasks/${batchId}/${encounterId}/supervisor-workspace/reject`,
            method: 'POST',
            body: {
              annotationIdToReject,
            },
          };
        },
        async onQueryStarted({ encounterId, batchId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            patchWorkspaceCache(dispatch, encounterId, batchId, data);
          } catch {
            /* cache patch skipped on error — UI keeps prior state */
          }
        },
      }),

      updateSuperviseAttributes: builder.mutation<
        ObjectResponse<SupervisorMutationResponse>,
        UpdateSupervisorAttributesRequest
      >({
        query: ({ annotationId, attributes, spans, evidenceIdsToDelete }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/supervisor/annotations/${annotationId}/attributes`,
            method: 'PUT',
            body: {
              attributes,
              spans: spans || [],
              ...(evidenceIdsToDelete?.length ? { evidenceIdsToDelete } : {}),
            },
          };
        },
        async onQueryStarted(_args, { dispatch, queryFulfilled, getState }) {
          const activeArgs = getActiveWorkspaceArgs(getState() as RootState);
          if (!activeArgs) return;
          try {
            const { data } = await queryFulfilled;
            patchWorkspaceCache(dispatch, activeArgs.encounterId, activeArgs.batchId, data);
          } catch {
            /* cache patch skipped on error — UI keeps prior state */
          }
        },
      }),

      supervisorRejectAndCreateAnnotation: builder.mutation<
        ObjectResponse<SupervisorMutationResponse>,
        SupervisorCreateAnnotationRequest
      >({
        query: ({
          encounterId,
          conflictId,
          annotationIdsToReject,
          conceptId,
          attributes,
          spans,
        }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/supervisor/annotations`,
            method: 'POST',
            body: {
              conceptId,
              conflictId,
              annotationIdsToReject,
              attributes,
              spans: spans || [],
            },
          };
        },
        async onQueryStarted(_args, { dispatch, queryFulfilled, getState }) {
          const activeArgs = getActiveWorkspaceArgs(getState() as RootState);
          if (!activeArgs) return;
          try {
            const { data } = await queryFulfilled;
            patchWorkspaceCache(dispatch, activeArgs.encounterId, activeArgs.batchId, data);
          } catch {
            /* cache patch skipped on error — UI keeps prior state */
          }
        },
      }),

      deleteResolvedAnnotation: builder.mutation<
        ObjectResponse<SupervisorMutationResponse>,
        { annotationId: string }
      >({
        query: ({ annotationId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/supervisor/annotations/${annotationId}`,
            method: 'DELETE',
          };
        },
        async onQueryStarted(_args, { dispatch, queryFulfilled, getState }) {
          const activeArgs = getActiveWorkspaceArgs(getState() as RootState);
          if (!activeArgs) return;
          try {
            const { data } = await queryFulfilled;
            patchWorkspaceCache(dispatch, activeArgs.encounterId, activeArgs.batchId, data);
          } catch {
            /* cache patch skipped on error — UI keeps prior state */
          }
        },
      }),

      addResolvedAnnotationSpans: builder.mutation<
        ObjectResponse<SupervisorMutationResponse>,
        AddSpanRequest
      >({
        query: ({ annotationId, spans }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/supervisor/annotations/${annotationId}/spans`,
            method: 'POST',
            body: {
              spans,
            },
          };
        },
        async onQueryStarted(_args, { dispatch, queryFulfilled, getState }) {
          const activeArgs = getActiveWorkspaceArgs(getState() as RootState);
          if (!activeArgs) return;
          try {
            const { data } = await queryFulfilled;
            patchWorkspaceCache(dispatch, activeArgs.encounterId, activeArgs.batchId, data);
          } catch {
            /* cache patch skipped on error — UI keeps prior state */
          }
        },
      }),

      supervisorAddFinalizedAnnotation: builder.mutation<
        ObjectResponse<SupervisorMutationResponse>,
        SupervisorAddFinalizedAnnotationRequest
      >({
        query: ({ encounterId, batchId, conceptId, attributes, spans, annotationIdsToDelete }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;
          return {
            url: `${tempPath}/supervisor/annotations/create`,
            method: 'POST',
            body: {
              encounterId,
              batchId,
              conceptId,
              attributes,
              spans: spans || [],
              ...(annotationIdsToDelete?.length ? { annotationIdsToDelete } : {}),
            },
          };
        },
        async onQueryStarted({ encounterId, batchId }, { dispatch, queryFulfilled }) {
          try {
            const { data } = await queryFulfilled;
            patchWorkspaceCache(dispatch, encounterId, batchId, data);
          } catch {
            /* cache patch skipped on error — UI keeps prior state */
          }
        },
      }),

      markSupervisionComplete: builder.mutation<
        ObjectResponse<CompleteSupervisionRequest>,
        CompleteSupervisionRequest
      >({
        query: ({ encounterId, batchId }) => {
          const basePath = getAnnotationToolBasePath();
          const tempPath = `/internal-message-gateway${basePath}`;

          return {
            url: `${tempPath}/supervisor/tasks/${batchId}/${encounterId}/mark-complete`,
            method: 'POST',
          };
        },
        invalidatesTags: [SUPERVISOR_TAG],
      }),
    }),
  });

export const {
  useGetSupervisorWorkspaceQuery,
  useResolveSupervisorConflictMutation,
  useUpdateSuperviseAttributesMutation,
  useSupervisorRejectAndCreateAnnotationMutation,
  useDeleteResolvedAnnotationMutation,
  useAddResolvedAnnotationSpansMutation,
  useRejectConflictAnnotationMutation,
  useMarkSupervisionCompleteMutation,
  useSupervisorAddFinalizedAnnotationMutation,
} = supervisorApiSlice;
